import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import moment from 'moment';
import { useParams, useSearchParams } from 'react-router-dom';
import { Collapse } from 'antd';
import { DownOutlined } from '@ant-design/icons';

import CardLoanTasks from 'components/Atoms/Cards/CardLoanTasks/CardLoanTasks';
import HeaderHome from 'scenes/Private/components/Menu/HeaderDashboard/HeaderDashboard';
import BarDashboard from 'scenes/Private/components/Menu/BarDashboard/BarDashboard';
import CardProfile from 'scenes/Private/components/CardProfile/CardProfile';
import ButtonTabsFilters from 'components/Atoms/ButtonTabsFilters/ButtonTabsFilters';
import DashboardLoader from 'components/Atoms/Loaders/Loader';

import { Loan, Task } from 'gql/graphql';
import useDashboard from './useDashboard';
import { TaskOpenModalContext } from '../Private';
import AddAdditionalDocument from './components/AddAdditionalDocument/AddAdditionalDocument';
import LoanProgress from 'components/Atoms/Progress/LoanProgress/LoanProgress';
import LoanDetails, { LoanDetailsModal } from './components/LoanDetails/LoanDetails';
import ModalBasic from 'components/Organisms/Modals/ModalBasic/ModalBasic';
import desktopIcon from 'assets/images/desktop.svg';

import {
  useTaskKeyFromUrl,
  useDashboardScroll,
  useDashboardActiveSections,
  useSmallScreenModal,
} from './hooks';

export const LoanContext = createContext<Loan | {}>({});
export const TasksContext = createContext<Task[] | []>([]);
export const ProgressContext = createContext<any>({});
export const CreateTaskResponseContext = createContext<{ value: string; setValue: any } | {}>({});
export const UpdateDashboardContext = createContext<{ value: string; setValue: any } | {}>({});
export const ViewLoanDetailsContext = createContext<{ state: string; setState: any } | {}>({});
export const OwnerSelectContext = createContext<{ value: string; setValue: (value: string) => void } | {}>({});

const Dashboard = () => {
  const { loanId: loanIdParam } = useParams<{ loanId?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { value: openContext, setHandler: setTaskOpenModal }: any = useContext(TaskOpenModalContext);

  // --- Estado propio del componente ---
  const [progressData, setProgressData] = useState();
  const [showLoanDetailsModal, setShowLoanDetailsModal] = useState(false);
  const [ownerSelect, setOwnerSelect] = useState<string>('');

  // --- Hooks de comportamiento (task por URL, scroll, secciones activas, modal mobile) ---
  useTaskKeyFromUrl(loanIdParam, searchParams, setSearchParams, setTaskOpenModal);
  const { sectionRefs, scrollToSection } = useDashboardScroll();
  const { isSmallScreen, setDisabledModalMobile } = useSmallScreenModal(true);

  const {
    loans,
    loanSelect,
    tasks,

    isCreateTaskResponse,
    setCreateTaskResponse,

    isUpdateDashboard,
    setUpdateDashboard,

    phaseNow,
    phaseSelect,
    setPhaseSelect,

    filter,
    setFilter,

    rulesTasksCards,
    rulesTasksCardsAll,
    tasksAll,
    phaseSection,
    initialOwnerId,
  } = useDashboard(ownerSelect);

  const { activeSectionKeys, setActiveSectionKeys } = useDashboardActiveSections({
    phaseSection,
    phaseNow,
    openContext,
    rulesTasksCardsAll,
  });

  useEffect(() => {
    if (initialOwnerId && !ownerSelect) {
      setOwnerSelect(initialOwnerId);
    }
  }, [initialOwnerId, ownerSelect]);

  useEffect(() => {
    if (showLoanDetailsModal && filter !== 'all') {
      setFilter('all');
    }
  }, [showLoanDetailsModal, filter, setFilter]);

  const handleShareLink = () => {
    navigator.clipboard.writeText(window.location.href);
    window.alert('Copied to clipboard!');
  };

  const handleToggleSection = useCallback(
    (sectionKey: string) => {
      const isCurrentlyOpen = activeSectionKeys.includes(sectionKey);
      const newActiveKeys = isCurrentlyOpen
        ? activeSectionKeys.filter((key) => key !== sectionKey)
        : [...activeSectionKeys, sectionKey];
      setActiveSectionKeys(newActiveKeys);
      if (!isCurrentlyOpen) {
        scrollToSection(sectionKey);
      }
    },
    [activeSectionKeys, setActiveSectionKeys, scrollToSection]
  );

  let firstPhaseSection: any, othersPhasesSection: any;

  if (phaseNow) {
    firstPhaseSection = phaseSection.find((item: any) => item.key === phaseNow?.key)
    othersPhasesSection = phaseSection.filter((item: any) => item.key !== phaseNow?.key)
  }

  if (!loans || !phaseSection.some((section: any) => Array.isArray(section.tasks))) {
    return <DashboardLoader />;
  }

  const date = loanSelect?.salesforceData?.closingDate

  // @ts-ignore
  const momentDate = date ? moment(date) : date

  const closingScheduled = loanSelect?.salesforceData?.closingScheduled;

  return (
    <>
      <ModalBasic
        visible={isSmallScreen}
        setVisible={() => { }}
        title="Unlock the full experience on desktop"
        description='Features and layouts are designed for wider viewing'
        submit={{ buttonLabel: "Share this link with yourself", onSubmit: handleShareLink }}
        icon={desktopIcon}
        className='modal-basic--small-screen'
        cancel={{
          buttonLabel: "Continue on mobile",
          onClick: () => setDisabledModalMobile(true)
        }}
      />

      <OwnerSelectContext.Provider value={{ value: ownerSelect, setValue: setOwnerSelect }}>
        <LoanContext.Provider value={loanSelect as Loan}>
          <TasksContext.Provider value={tasksAll as Task[]}>
            <ProgressContext.Provider value={progressData}>
              <CreateTaskResponseContext.Provider value={{ value: isCreateTaskResponse, setValue: setCreateTaskResponse }}>
                <UpdateDashboardContext.Provider value={{ value: isUpdateDashboard, setValue: setUpdateDashboard }}>
                  <ViewLoanDetailsContext.Provider value={{ state: showLoanDetailsModal, setState: setShowLoanDetailsModal }}>
                    <LoanDetailsModal
                      visible={showLoanDetailsModal}
                      closeModal={() => setShowLoanDetailsModal(false)}
                    >
                      <LoanDetails loanSelected={loanSelect} onClose={() => setShowLoanDetailsModal(false)} />
                    </LoanDetailsModal>
                    <div className="Dashboard">
                      <HeaderHome />
                      <div className="Dashboard--container">
                        <BarDashboard />
                        <div className="Dashboard__column">
                          <div className="Dashboard__head">
                            <div className="content">
                              <p className="body-regular">
                                {
                                  !closingScheduled ?
                                    "Est. Closing:"
                                    :
                                    "Scheduled Closing:"
                                }
                                {date ?
                                  <span className="body-regular-bold">
                                    {' '}
                                    {momentDate?.format('MM/DD/YY')}
                                  </span>
                                  :
                                  <span className="body-regular-bold">
                                    {' '}
                                    PENDING
                                  </span>
                                }
                              </p>
                              <h1 className="h1">{loanSelect?.name}</h1>
                              <div className="content-progress">
                                <span className="body-x-small">Loan stage</span>
                                <LoanProgress
                                  loan={loanSelect}
                                  phaseSelect={phaseSelect}
                                  rulesTasksCards={rulesTasksCards}
                                  handlerSetPhaseSelect={setPhaseSelect}
                                  handlerSetTaskOpenModal={setTaskOpenModal}
                                  handlerSetFilter={setFilter}
                                  handlerSetProgressData={setProgressData}
                                  setShowLoanDetailsModal={setShowLoanDetailsModal}
                                />
                              </div>
                            </div>
                            <CardProfile
                              name="Brian Hurlston"
                              email="processing@constlending.com"
                              phone="(203) 423-3534"
                            />
                          </div>
                          <div className="Dashboard__label">
                            <p className="body-small">
                              <span className="body-small-w4">
                                {loanSelect?.type}
                                {loanSelect?.purpose ? (' | ' + loanSelect?.purpose + ' | ') : ''}
                              </span>{' '}
                              <span className="body-small-w6" onClick={() => setShowLoanDetailsModal(true)} style={{ cursor: "pointer", color: 'var(--blue-60)' }}>
                                See Loan Details and Docs {'->'}
                              </span>
                            </p>
                          </div>


                        <div
                          ref={(el) => sectionRefs.current[firstPhaseSection.key] = el}
                          className={`first-section first-section-${firstPhaseSection?.state !== 'DO' && firstPhaseSection?.tasks.length > 0 && 'DONE'}`}
                        >
                          <div className="Dashboard__head Dashboard__head-tabs">
                            <div
                              className="box-title"
                              style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12 }}
                            >
                              {firstPhaseSection?.tasks.length > 0 && (
                                <>
                                  <div>
                                    <p className="red-title">
                                      {firstPhaseSection.state === 'DO' ? 'DO NOW' : 'DONE'}
                                    </p>
                                    <h1 className="h2" style={{ cursor: 'default' }}>{firstPhaseSection.label} </h1>
                                  </div>
                                  {/* Fecha e ícono animado */}
                                  <div
                                    className="box-title__date-arrow"
                                    style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                                    onClick={() => handleToggleSection(firstPhaseSection.key)}
                                  >
                                    <span
                                      className={`box-title__arrow${activeSectionKeys.includes(firstPhaseSection.key) ? ' open' : ''}`}
                                    >
                                      <DownOutlined />
                                    </span>
                                  </div>
                                </>
                              )}
                            </div>
                            <ButtonTabsFilters phaseSelect={phaseSelect} tasks={tasksAll} filter={filter} handlerChange={setFilter} />
                            <div className="content-link">
                              <AddAdditionalDocument />
                            </div>
                          </div>

                          <Collapse
                            activeKey={activeSectionKeys.includes(firstPhaseSection.key) ? [firstPhaseSection.key] : []}
                            onChange={() => handleToggleSection(firstPhaseSection.key)}
                            style={{ background: 'transparent', marginTop: 16 }}
                            expandIconPosition="end"
                            className="no-collapse-header"
                          >
                            <Collapse.Panel header={null} key={firstPhaseSection.key} collapsible="disabled">
                              <div className="content-flex-wrap">
                                {loans && firstPhaseSection?.tasks?.map((task: any) => {
                                  const { key, ..._task } = task;
                                  return (
                                    <section key={task.key} className="content-flex-wrap-item">
                                      <CardLoanTasks taskKey={task.key} {..._task} />
                                    </section>
                                  )
                                })}
                              </div>
                            </Collapse.Panel>
                          </Collapse>
                        </div>

                        {othersPhasesSection.map((item: any) => (
                          item.tasks.length > 0 && (
                            <div
                              key={item.key}
                              ref={(el) => sectionRefs.current[item.key] = el}
                              className={`second-section second-section-${item.state}`}
                            >
                              <div className="Dashboard__head Dashboard__head-tabs">
                                <div
                                  className="box-title"
                                  style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12 }}
                                >
                                  <div>
                                    <p className="red-title">
                                      {item.state === 'DO' && item.key !== 'due-diligence'
                                        ? 'DO NOW'
                                        : item.state === 'DO_LATER' || (item.state === 'DO' && item.key === 'due-diligence')
                                          ? 'DO LATER'
                                          : 'DONE'}
                                    </p>
                                    <h1 className="h2" style={{ cursor: 'default' }}>{item.label}</h1>
                                  </div>
                                  <div
                                    className="box-title__date-arrow"
                                    style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                                    onClick={() => handleToggleSection(item.key)}
                                  >
                                    <span
                                      className={`box-title__arrow${activeSectionKeys.includes(item.key) ? ' open' : ''}`}
                                    >
                                      <DownOutlined />
                                    </span>
                                  </div>
                                </div>
                              </div>
                              <Collapse
                                activeKey={activeSectionKeys.includes(item.key) ? [item.key] : []}
                                onChange={() => handleToggleSection(item.key)}
                                style={{ background: 'transparent', marginTop: 16 }}
                                expandIconPosition="end"
                                className="no-collapse-header"
                              >
                                <Collapse.Panel header={null} key={item.key} collapsible="disabled">
                                  <div className="content-flex-wrap">
                                    {loans && item?.tasks?.map((task: any) => {
                                      const { key, ..._task } = task;
                                      return (
                                        <section key={task.key} className="content-flex-wrap-item">
                                          <CardLoanTasks taskKey={task.key} {..._task} />
                                        </section>
                                      )
                                    })}
                                  </div>
                                </Collapse.Panel>
                              </Collapse>
                            </div>
                          )
                        ))}
                        </div>
                      </div>
                    </div>
                  </ViewLoanDetailsContext.Provider>
                </UpdateDashboardContext.Provider>
              </CreateTaskResponseContext.Provider>
            </ProgressContext.Provider>
          </TasksContext.Provider>
        </LoanContext.Provider>
      </OwnerSelectContext.Provider>
    </>
  );
};
export default Dashboard;
